import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { clearToken, api } from "../lib/api";
import { LogOut, Radio, Target, Building2, Beaker, User, Home, DollarSign, Users, FileText, Sparkles, Settings as SettingsIcon } from "lucide-react";
import CaptureFAB from "./CaptureFAB";
import AskFAB from "./AskFAB";
import WakeSally from "./WakeSally";
import { useEditMode } from "../lib/EditModeContext";

const NAV = [
  { to: "/", label: "WAR ROOM", code: "01", icon: Radio, end: true, business: null },
  { to: "/money", label: "MONEY", code: "02", icon: DollarSign, business: null },
  { to: "/waiting-on", label: "WAITING ON", code: "03", icon: Users, business: null },
  { to: "/reports", label: "REPORTS", code: "04", icon: FileText, business: null },
  { to: "/sally-history", label: "SALLY · HISTORY", code: "05", icon: Sparkles, business: null },
  { to: "/pyn-capital", label: "PYN CAPITAL", code: "06", icon: Target, business: "PYN CAPITAL" },
  { to: "/doa", label: "DOA / PRO PARTNER", code: "07", icon: Building2, business: "DOA / PRO PARTNER" },
  { to: "/biovault", label: "BIOVAULT", code: "08", icon: Beaker, business: "BIOVAULT" },
  { to: "/personal", label: "PERSONAL", code: "09", icon: User, business: "PERSONAL" },
  { to: "/settings", label: "SETTINGS", code: "10", icon: SettingsIcon, business: null },
];

const Shell = ({ children }) => {
  const nav = useNavigate();
  const location = useLocation();
  const { editMode, toggleEditMode } = useEditMode();
  const [counts, setCounts] = useState({}); // { biz: { total, p1 } }
  const [flashing, setFlashing] = useState({}); // { biz: true }
  const [lastSync, setLastSync] = useState(null);
  const prevP1 = React.useRef({});

  const loadCounts = async () => {
    try {
      const res = await api.todoistCounts();
      // Support both new {counts, last_synced_at, reconciled} shape and legacy plain map
      const isNewShape = res && typeof res === "object" && "counts" in res;
      const c = isNewShape ? res.counts || {} : res || {};
      const synced = isNewShape ? res.last_synced_at : null;
      const reconciled = isNewShape ? res.reconciled : null;

      // Detect new P1s per business
      const newFlashing = {};
      Object.entries(c).forEach(([biz, v]) => {
        const cur = v?.p1 || 0;
        const prev = prevP1.current[biz] ?? cur;
        if (cur > prev) newFlashing[biz] = true;
        prevP1.current[biz] = cur;
      });
      setCounts(c);
      if (synced) setLastSync(synced);
      if (Object.keys(newFlashing).length) {
        setFlashing((f) => ({ ...f, ...newFlashing }));
        setTimeout(() => {
          setFlashing((f) => {
            const next = { ...f };
            Object.keys(newFlashing).forEach((k) => delete next[k]);
            return next;
          });
        }, 4000);
      }

      // If inbound reconciliation touched anything, tell listening pages to refresh
      if (reconciled && (reconciled.top3 > 0 || reconciled.subtasks > 0)) {
        window.dispatchEvent(new CustomEvent("operator:sync", { detail: reconciled }));
      }
    } catch {
      setCounts({});
    }
  };

  // Refresh counts on route change so promotes/creates update the badges
  useEffect(() => { loadCounts(); }, [location.pathname]);

  // Poll every 60s so the sidebar stays honest while you work
  useEffect(() => {
    const iv = setInterval(loadCounts, 60_000);
    return () => clearInterval(iv);
  }, []);

  const logout = () => {
    clearToken();
    nav("/login");
  };

  const syncedLabel = React.useMemo(() => {
    if (!lastSync) return null;
    try {
      const d = new Date(lastSync);
      return `SYNCED · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}`;
    } catch {
      return null;
    }
  }, [lastSync]);

  const Badge = ({ biz }) => {
    const v = counts[biz];
    const n = v?.total || 0;
    if (n <= 0) return null;
    const flash = flashing[biz];
    return (
      <span
        data-testid={`nav-badge-${biz}`}
        className={`ml-auto op-mono text-[9px] tracking-[0.14em] px-1.5 py-0.5 border ${
          flash
            ? "border-[#F5A623] text-[#F5A623] bg-[rgba(245,166,35,0.08)] op-p1-flash"
            : "border-[rgba(0,229,255,0.3)] text-[#00E5FF] bg-[rgba(0,229,255,0.05)]"
        }`}
      >
        {n > 99 ? "99+" : n}
      </span>
    );
  };

  return (
    <div className="min-h-screen relative flex">
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex md:flex-col shrink-0 w-[240px] border-r border-white/[0.05] relative z-10"
        data-testid="sidebar"
      >
        <div className="px-6 pt-8 pb-6">
          <div className="op-microcopy mb-2">SYSTEM</div>
          <div className="text-white text-xl font-medium tracking-tight">OPERATOR OS</div>
          <div className="flex items-center gap-2 mt-2">
            <span className="op-pulse-dot" />
            <span className="op-tag">ONLINE</span>
          </div>
          {syncedLabel && (
            <div className="op-tag mt-1 text-[9px] tracking-[0.22em]" data-testid="last-sync">
              {syncedLabel}
            </div>
          )}
        </div>
        <div className="op-hairline mx-4" />
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.code}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-3 rounded-sm border transition-colors duration-300 ${
                  isActive
                    ? "border-[rgba(0,229,255,0.35)] bg-white/[0.02] text-white"
                    : "border-transparent text-[#A0A5B5] hover:text-white hover:border-white/10"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`op-mono text-[10px] tracking-[0.24em] ${isActive ? "text-[#00E5FF]" : "text-[#5E657D]"}`}>
                    {n.code}
                  </span>
                  <n.icon size={14} strokeWidth={1.4} className={isActive ? "text-[#00E5FF]" : ""} />
                  <span className="text-[12px] tracking-[0.14em] uppercase font-medium">{n.label}</span>
                  {n.business && <Badge biz={n.business} />}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="op-hairline mx-4" />
        <div className="p-4 space-y-2">
          <button
            onClick={toggleEditMode}
            data-testid="edit-mode-toggle"
            data-active={editMode}
            className="op-edit-toggle"
            title="Toggle inline text editing across the app"
          >
            <span className="inline-flex items-center gap-2">
              <span className="op-edit-dot" />
              EDIT MODE
            </span>
            <span>{editMode ? "ON" : "OFF"}</span>
          </button>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-between px-3 py-2 text-[#5E657D] hover:text-white transition-colors duration-300"
          >
            <span className="op-tag">SIGN OUT</span>
            <LogOut size={14} strokeWidth={1.4} />
          </button>
        </div>
      </aside>

      <main className="flex-1 relative z-10 pt-14 md:pt-0 pb-24 md:pb-8">{children}</main>

      {/* Mobile top bar */}
      <header
        className="md:hidden fixed top-0 left-0 right-0 z-30 h-12 flex items-center justify-between px-4 bg-[#0A0C14]/92 backdrop-blur-md border-b border-white/[0.06]"
        data-testid="mobile-top-bar"
      >
        <div className="flex items-center gap-2">
          <span className="op-pulse-dot" />
          <span className="op-mono text-[10px] tracking-[0.24em] text-white">OPERATOR OS</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleEditMode}
            data-testid="edit-mode-toggle-mobile"
            data-active={editMode}
            className="op-mono text-[10px] tracking-[0.24em] px-2 py-1 border transition-colors"
            style={{
              color: editMode ? "#00E5FF" : "#A0A5B5",
              borderColor: editMode ? "rgba(0,229,255,0.55)" : "rgba(255,255,255,0.08)",
              background: editMode ? "rgba(0,229,255,0.06)" : "transparent",
            }}
          >
            EDIT · {editMode ? "ON" : "OFF"}
          </button>
          <button
            onClick={logout}
            data-testid="logout-btn-mobile"
            className="op-mono text-[10px] tracking-[0.24em] px-2 py-1 border border-white/[0.08] text-[#A0A5B5] hover:text-white"
          >
            <LogOut size={12} strokeWidth={1.4} />
          </button>
        </div>
      </header>

      {/* Mobile bottom nav — horizontally scrollable with icon + label */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#0A0C14]/95 backdrop-blur-md border-t border-white/[0.06] overflow-x-auto no-scrollbar"
        data-testid="bottom-nav"
      >
        <div className="flex min-w-full">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`bottom-nav-${n.code}`}
              className={({ isActive }) =>
                `shrink-0 basis-[72px] flex flex-col items-center justify-center py-2.5 gap-1 ${
                  isActive ? "text-[#00E5FF]" : "text-[#5E657D]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className="relative">
                    <n.icon size={16} strokeWidth={1.4} />
                    {n.business && (counts[n.business]?.total || 0) > 0 && (
                      <span
                        className={`absolute -top-1.5 -right-2 op-mono text-[8px] px-1 border bg-[#0A0C14] ${
                          flashing[n.business]
                            ? "border-[#F5A623] text-[#F5A623] op-p1-flash"
                            : "border-[rgba(0,229,255,0.4)] text-[#00E5FF]"
                        }`}
                      >
                        {counts[n.business].total > 99 ? "99+" : counts[n.business].total}
                      </span>
                    )}
                  </div>
                  <span className="op-mono text-[8px] tracking-[0.14em] whitespace-nowrap">
                    {n.label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      <CaptureFAB />
      <AskFAB />
      <WakeSally />
    </div>
  );
};

export default Shell;
